import supabase from './supabaseClient';

const APP_SOURCE = 'meal-manager';
const APP_LABELS = {
  'activity-planner': 'Activity Planner',
  'meal-manager': 'Meal Manager',
};
const META_REGEX = /^\[\[app:([a-z-]+)\]\]\s*\[\[url:([^\]]+)\]\]\s*/i;
const TICKET_META_REGEX = /^\[\[app:([a-z-]+)\]\]\s*\[\[ticket:([^\]]+)\]\]\s*/i;

const defaultSourceUrl = (type) => {
  if (type === 'meal_pending') return '/super-admin/meal-approvals';
  if (type === 'meal_approved' || type === 'meal_rejected') return '/meals-management';
  if (type === 'onboarding_completed') return '/super-admin?tab=onboarding';
  return '/';
};

export const getCommunityPostUrl = (postId = null) => {
  const query = postId ? `?post=${encodeURIComponent(postId)}` : '';
  return `/community${query}`;
};

export const addNotificationMeta = ({ message, appSource = APP_SOURCE, sourceUrl = '/' }) => {
  const baseMessage = (message || '').trim();
  if (!baseMessage) return `[[app:${appSource}]] [[url:${sourceUrl}]]`;
  return `[[app:${appSource}]] [[url:${sourceUrl}]] ${baseMessage}`;
};

export const parseNotificationMeta = (message) => {
  const raw = String(message || '');
  const matched = raw.match(META_REGEX);
  if (matched) {
    const sourceApp = (matched[1] || '').toLowerCase();
    const sourceUrl = matched[2] || null;
    return {
      sourceApp,
      sourceLabel: APP_LABELS[sourceApp] || sourceApp,
      sourceUrl,
      ticketId: null,
      cleanMessage: raw.replace(META_REGEX, '').trim(),
    };
  }

  const ticketMatched = raw.match(TICKET_META_REGEX);
  if (ticketMatched) {
    const sourceApp = (ticketMatched[1] || '').toLowerCase();
    return {
      sourceApp,
      sourceLabel: APP_LABELS[sourceApp] || sourceApp,
      sourceUrl: null,
      ticketId: ticketMatched[2] || null,
      cleanMessage: raw.replace(TICKET_META_REGEX, '').trim(),
    };
  }

  return {
    sourceApp: null,
    sourceLabel: null,
    sourceUrl: null,
    ticketId: null,
    cleanMessage: raw,
  };
};

// ─── Fetch notifications for the current user ──────────────────────────────
export const getNotifications = async (userId, { limit = 30, unreadOnly = false } = {}) => {
  let query = supabase
    .from('community_notifications')
    .select('*')
    .eq('recipient_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (unreadOnly) {
    query = query.eq('is_read', false);
  }

  const { data, error } = await query;
  if (error) {
    console.warn('getNotifications error:', error);
    return [];
  }
  return data || [];
};

// ─── Get unread count ──────────────────────────────────────────────────────
export const getUnreadCount = async (userId) => {
  const { count, error } = await supabase
    .from('community_notifications')
    .select('*', { count: 'exact', head: true })
    .eq('recipient_id', userId)
    .eq('is_read', false);

  if (error) {
    console.warn('getUnreadCount error:', error);
    return 0;
  }
  return count || 0;
};

// ─── Mark a single notification as read ────────────────────────────────────
export const markAsRead = async (notificationId) => {
  const { error } = await supabase
    .from('community_notifications')
    .update({ is_read: true })
    .eq('id', notificationId);

  if (error) console.warn('markAsRead error:', error);
};

// ─── Mark all notifications as read ────────────────────────────────────────
export const markAllAsRead = async (userId) => {
  const { error } = await supabase
    .from('community_notifications')
    .update({ is_read: true })
    .eq('recipient_id', userId)
    .eq('is_read', false);

  if (error) console.warn('markAllAsRead error:', error);
};

// ─── Delete a notification ─────────────────────────────────────────────────
export const deleteNotification = async (notificationId) => {
  const { error } = await supabase
    .from('community_notifications')
    .delete()
    .eq('id', notificationId);

  if (error) console.warn('deleteNotification error:', error);
};

// ─── Create a notification ─────────────────────────────────────────────────
// This is called from the vote and comment flows.
// It does NOT notify the user about their own actions.
export const createNotification = async ({
  recipientId,
  actorId,
  type,
  postId = null,
  commentId = null,
  message,
  sourceUrl: sourceUrlOverride = null,
}) => {
  // Don't notify yourself
  if (recipientId === actorId) return null;

  const sourceUrl = sourceUrlOverride
    || (postId ? getCommunityPostUrl(postId) : defaultSourceUrl(type));
  const taggedMessage = addNotificationMeta({ message, sourceUrl });

  // Do NOT use .select().single() here — the INSERT is done by the actor
  // but the SELECT RLS policy restricts reads to recipient_id = auth.uid(),
  // so the actor cannot read back the row they just created for someone else.
  // Using .select() would cause PostgreSQL to roll back the INSERT entirely.
  const { error } = await supabase
    .from('community_notifications')
    .insert([{
      recipient_id: recipientId,
      actor_id: actorId,
      type,
      post_id: postId,
      comment_id: commentId,
      message: taggedMessage,
    }]);

  if (error) {
    console.error('createNotification error:', error);
    return null;
  }
  return true;
};

export const broadcastPostNotification = async ({ postId, customMessage = null }) => {
  const taggedMessage = addNotificationMeta({
    message: customMessage || 'Check out this community post',
    sourceUrl: getCommunityPostUrl(postId),
  });

  const { data, error } = await supabase.rpc('broadcast_community_post_notification', {
    p_post_id: postId,
    p_custom_message: taggedMessage,
  });

  if (error) {
    console.error('broadcastPostNotification error:', error);
    throw error;
  }

  return Number(data || 0);
};

// ─── Subscribe to real-time notifications ──────────────────────────────────
// Returns a channel subscription — call .unsubscribe() to clean up.
export const subscribeToNotifications = (userId, onNewNotification, { channelId } = {}) => {
  const channel = supabase
    .channel(channelId || `notifications:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'community_notifications',
        filter: `recipient_id=eq.${userId}`,
      },
      (payload) => {
        onNewNotification?.(payload.new);
      }
    )
    .subscribe();

  return channel;
};
